import { computed, observable, runInAction } from "mobx";
import { isEmpty } from "../../../utils";
import BaseChannelSettings from "./BaseChannelSettings";
import Entity from "../../Entity";

export enum Wazzup24ChatType {
    WhatsApp = 'whatsapp',
    WhatsGroup = 'whatsgroup ',
    Instagram = 'instagram',
    Telegram = 'telegram',
    TgGroup = 'telegroup',
    Vk = 'vk',
    Max = 'max'
}

export enum Wazzup24TransportType {
    WhatsApp = 'whatsapp',
    WAPI = 'wapi',
    Instagram = 'instagram',
    Telegram = 'telegram',
    TgAPI = 'tgapi',
    Vk = 'vk',
	Max = 'max'
}

export class Wazzup24Channel extends Entity {
    constructor(channel?: Partial<Wazzup24Channel>) {
        super();
        if (channel) this.update(channel);
    }

    @observable name: string;
    @observable plainId: string;
    @observable transportType: Wazzup24TransportType;
    
    @computed get chatType(): Wazzup24ChatType {
        switch (this.transportType) {
            case Wazzup24TransportType.WhatsApp:
            case Wazzup24TransportType.WAPI:
                return Wazzup24ChatType.WhatsApp
            case Wazzup24TransportType.Telegram:
            case Wazzup24TransportType.TgAPI:
                return Wazzup24ChatType.Telegram
            case Wazzup24TransportType.Instagram:
                return Wazzup24ChatType.Instagram
            case Wazzup24TransportType.Vk:
                return Wazzup24ChatType.Vk
			case Wazzup24TransportType.Max:
				return Wazzup24ChatType.Max
            default:
                return Wazzup24ChatType.Telegram
        }
    }

    static fromJson(json: any): Wazzup24Channel {
        return new Wazzup24Channel({
            ...json,
        });
    }

    update(channel: Partial<Wazzup24Channel>) {
        super.update(channel);
    }
}

export default class Wazzup24ChannelSettings extends BaseChannelSettings {
    @observable channelName: string;
    @observable channelId: string;
    @observable plainId: string;
    @observable transportType: Wazzup24TransportType;
    
    @computed get chatType(): Wazzup24ChatType {
        switch (this.transportType) {
            case Wazzup24TransportType.WhatsApp:
            case Wazzup24TransportType.WAPI:
                return Wazzup24ChatType.WhatsApp
            case Wazzup24TransportType.Telegram:
            case Wazzup24TransportType.TgAPI:
                return Wazzup24ChatType.Telegram
            case Wazzup24TransportType.Instagram:
                return Wazzup24ChatType.Instagram
            case Wazzup24TransportType.Vk:
                return Wazzup24ChatType.Vk
            default:
                return Wazzup24ChatType.Telegram
        }
    }

    constructor(settings?: Partial<Wazzup24ChannelSettings>) {
        super();
        if (settings) this.update(settings);
    }

    get channel(): Wazzup24Channel | null {
        if (!this.hasChannel) return null;
        return new Wazzup24Channel({
            id: this.channelId,
            name: this.channelName,
            plainId: this.plainId,
            transportType: this.transportType,
        });
    }

    set channel(channel: Wazzup24Channel | null) {
        if (!channel) return; // ignore this case
        runInAction(() => {
            this.channelName = channel.name;
            this.channelId = channel.id;
            this.plainId = channel.plainId;
            this.transportType = channel.transportType;
        });
    }

    get isValid() {
        return !isEmpty(this.accessToken) && this.hasChannel;
    }

    get hasChannel() {
        return !isEmpty(this.channelId) && !isEmpty(this.plainId);
    }

    update(entity: Partial<Wazzup24ChannelSettings>, allowUndefined = false) {
        super.update(entity, allowUndefined);
    }

    hasChanges(settings: Wazzup24ChannelSettings) {
        return super.hasChanges(settings)
            || this.channelId != settings.channelId
            || this.plainId != settings.plainId
            || this.channelName != settings.channelName
            || this.transportType != settings.transportType;
    }

    clone() {
        return new Wazzup24ChannelSettings(this);
    }
}